import React, { useMemo } from 'react';
import { ASSIGNMENT_TYPES } from '../constants/editJobConstants';
import AssignmentAvailabilityWarning from '../../team-availability/AssignmentAvailabilityWarning';

const AssignmentSection = ({ 
  assignmentType,
  setAssignmentType,
  formData,
  handleChange,
  sortedActiveUsers,
  selectedTeamId,
  setSelectedTeamId,
  teamsData,
  assignmentDataLoading = false,
  usersLoading = false,
  teamsLoading = false,
  scheduledDate = null
}) => {
  const isIndividualAssignment = assignmentType === ASSIGNMENT_TYPES.INDIVIDUAL;
  const isTeamAssignment = assignmentType === ASSIGNMENT_TYPES.TEAM;
  const isIndividualOptionsLoading = isIndividualAssignment && (assignmentDataLoading || usersLoading);
  const isTeamOptionsLoading = isTeamAssignment && (assignmentDataLoading || teamsLoading);
  const isAssignmentLoading = isIndividualOptionsLoading || isTeamOptionsLoading;

  const availabilityDates = useMemo(() => (scheduledDate ? [scheduledDate] : []), [scheduledDate]);

  const individualUserIds = useMemo(() => {
    if (!isIndividualAssignment) return [];
    if (!formData?.assignedToId) return [];
    return [formData.assignedToId];
  }, [isIndividualAssignment, formData?.assignedToId]);

  const teamMemberIds = useMemo(() => {
    if (!isTeamAssignment || !selectedTeamId) return [];
    const team = teamsData?.activeTeams?.find(
      (candidate) => String(candidate.id) === String(selectedTeamId)
    );
    return Array.isArray(team?.memberIds) ? team.memberIds : [];
  }, [isTeamAssignment, selectedTeamId, teamsData]);
  const assignmentTypeLoadingClassName = assignmentDataLoading ? 'cursor-wait opacity-75' : 'cursor-pointer';
  const assignmentSelectClassName = `mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-600 p-2 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm rounded-md ${
    isAssignmentLoading ? 'bg-gray-100 cursor-wait text-gray-600' : ''
  }`;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Assignment
        </label>
        {isAssignmentLoading && (
          <span className="inline-flex items-center text-xs font-medium text-sky-700">
            <svg className="animate-spin h-3.5 w-3.5 mr-1.5 text-sky-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading assignment
          </span>
        )}
      </div>
      
      {/* Assignment type toggle */}
      <div className="flex space-x-6 mb-3">
        {/* Individual Assignment Option */}
        <label className={`flex items-center p-3 w-1/2 border rounded-lg transition-all duration-200 ${assignmentTypeLoadingClassName} ${
          assignmentType === ASSIGNMENT_TYPES.INDIVIDUAL
            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
        }`}>
          <input
            type="radio"
            value={ASSIGNMENT_TYPES.INDIVIDUAL}
            checked={assignmentType === ASSIGNMENT_TYPES.INDIVIDUAL}
            onChange={(e) => setAssignmentType(e.target.value)}
            disabled={assignmentDataLoading}
            className="sr-only"
          />
          <div className={`mr-3 p-1.5 rounded-full ${
            assignmentType === ASSIGNMENT_TYPES.INDIVIDUAL
              ? 'bg-indigo-100'
              : 'bg-gray-100'
          }`}>
            <svg 
              className={`w-5 h-5 ${
                assignmentType === ASSIGNMENT_TYPES.INDIVIDUAL
                  ? 'text-indigo-600'
                  : 'text-gray-500'
              }`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="2" 
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" 
              />
            </svg>
          </div>
          <span className="text-sm font-medium">Individual</span>
          {/* Selection indicator */}
          {assignmentType === ASSIGNMENT_TYPES.INDIVIDUAL && (
            <div className="ml-3">
              <div className="w-4 h-4 bg-indigo-600 rounded-full flex items-center justify-center">
                <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          )}
        </label>

        {/* Team Assignment Option */}
        <label className={`flex items-center p-3 w-1/2 border rounded-lg transition-all duration-200 ${assignmentTypeLoadingClassName} ${
          assignmentType === ASSIGNMENT_TYPES.TEAM
            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
        }`}>
          <input
            type="radio"
            value={ASSIGNMENT_TYPES.TEAM}
            checked={assignmentType === ASSIGNMENT_TYPES.TEAM}
            onChange={(e) => setAssignmentType(e.target.value)}
            disabled={assignmentDataLoading}
            className="sr-only"
          />
          <div className={`mr-3 p-1.5 rounded-full ${
            assignmentType === ASSIGNMENT_TYPES.TEAM
              ? 'bg-indigo-100'
              : 'bg-gray-100'
          }`}>
            <svg 
              className={`w-5 h-5 ${
                assignmentType === ASSIGNMENT_TYPES.TEAM
                  ? 'text-indigo-600'
                  : 'text-gray-500'
              }`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="2" 
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" 
              />
            </svg>
          </div>
          <span className="text-sm font-medium">Team</span>
          {/* Selection indicator */}
          {assignmentType === ASSIGNMENT_TYPES.TEAM && (
            <div className="ml-3">
              <div className="w-4 h-4 bg-indigo-600 rounded-full flex items-center justify-center">
                <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          )}
        </label>
      </div>

      {/* Individual assignment dropdown */}
      {isIndividualAssignment && (
        <div>
          <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700">
            Assigned To
          </label>
          <select
            id="assignedTo"
            name="assignedToId"
            value={isIndividualOptionsLoading ? '__loading__' : formData.assignedToId}
            onChange={handleChange}
            disabled={isIndividualOptionsLoading}
            className={assignmentSelectClassName}
          >
            {isIndividualOptionsLoading ? (
              <option value="__loading__">Loading assigned team member...</option>
            ) : (
              <>
                <option value="">Unassigned</option>
                {sortedActiveUsers.map(user => (
                  <option key={user.id} value={String(user.id)}>
                    {user.name} ({user.role})
                  </option>
                ))}
              </>
            )}
          </select>
          <AssignmentAvailabilityWarning
            userIds={individualUserIds}
            dates={availabilityDates}
          />
        </div>
      )}

      {/* Team assignment dropdown */}
      {isTeamAssignment && (
        <div>
          <label htmlFor="assignedTeam" className="block text-sm font-medium text-gray-700">
            Assigned Team
          </label>
          <select
            id="assignedTeam"
            value={isTeamOptionsLoading ? '__loading__' : selectedTeamId}
            onChange={(e) => setSelectedTeamId(e.target.value)}
            disabled={isTeamOptionsLoading}
            className={assignmentSelectClassName}
          >
            {isTeamOptionsLoading ? (
              <option value="__loading__">Loading assigned team...</option>
            ) : (
              <>
                <option value="">Unassigned</option>
                {teamsData?.activeTeams?.map(team => (
                  <option key={team.id} value={String(team.id)}>
                    {team.name} ({team.memberCount || 0} members)
                  </option>
                ))}
              </>
            )}
          </select>
          {isTeamOptionsLoading ? (
            <div className="mt-2 text-sm text-sky-700">
              <svg className="animate-spin inline h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading current team assignment
            </div>
          ) : selectedTeamId ? (
            <>
              <div className="mt-2 text-sm text-blue-600">
                <svg className="inline h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" 
                  />
                </svg>
                This job will be assigned to all team members
              </div>
              <AssignmentAvailabilityWarning
                userIds={teamMemberIds}
                dates={availabilityDates}
                subtitle="Some team members are flagged as unavailable on this date. You can still assign the whole team, but be aware of the conflicts below."
              />
            </>
          ) : (
            <div className="mt-2 text-sm text-gray-500">
              <svg className="inline h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                />
              </svg>
              Job will be unassigned from any team
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AssignmentSection;
